import { MessageCircle, MessageSquareText } from 'lucide-react'

import {
  answerableOptionGroups,
  askUserCardVariant,
  isAnswerableStagedAction,
} from '@renderer/chat/integrations/answerable'
import { AskUserBadge, AskUserOptionsPreview } from '@renderer/chat/integrations/ask-user-card'
import { ActionContentPreview } from '@renderer/components/action-content-preview'
import { ActionIconSlot } from '@renderer/components/action-icon-slot'
import { ActionPreview } from '@renderer/overlay/Sidepanel/components/action-preview'
import { CellPreviewCard, HOVER_CARD_TRIGGER } from '@renderer/overlay/Sidepanel/components/cell-preview-card'
import { ConversationSkillIcon } from '@renderer/overlay/Sidepanel/components/conversation-skill-icon'
import {
  actionPreviewProps,
  useActionRowModel,
} from '@renderer/overlay/Sidepanel/hooks/staged-actions/use-action-row-model'
import type { StagedAction } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { isRegularChat, type SidepanelChat } from '@renderer/overlay/hooks/running-chat-selection'
import { getActionIcon, getActionTitle } from '@renderer/overlay/Sidepanel/utils/activity-helpers'

interface ForYouChatPreviewProps {
  chat: SidepanelChat
  actions: Array<StagedAction>
  sourceLabel: string
  onOpen: () => void
}

export function ForYouChatPreview({ chat, actions, sourceLabel, onOpen }: ForYouChatPreviewProps) {
  const singleAction = actions.length === 1 ? actions[0] : undefined

  if (singleAction && !isRegularChat(chat)) {
    return <ForYouSingleActionPreview chat={chat} action={singleAction} sourceLabel={sourceLabel} onOpen={onOpen} />
  }

  return <ForYouConversationPreview chat={chat} actions={actions} onOpen={onOpen} />
}

function ForYouSingleActionPreview({
  chat,
  action,
  sourceLabel,
  onOpen,
}: {
  chat: SidepanelChat
  action: StagedAction
  sourceLabel: string
  onOpen: () => void
}) {
  const model = useActionRowModel(action, { sourceLabel, eventSource: 'for-you' })
  const previewProps = actionPreviewProps(model, action, () => model.openActionView(true, HOVER_CARD_TRIGGER), onOpen)
  const icon = isRegularChat(chat) ? <MessageCircle className="size-4" /> : undefined

  if (!previewProps) {
    return <CellPreviewCard.Shell icon={icon ?? getActionIcon(action)} title={getActionTitle(action)} onOpen={onOpen} />
  }

  return <ActionPreview {...previewProps} icon={icon} />
}

function ForYouConversationPreview({
  chat,
  actions,
  onOpen,
}: {
  chat: SidepanelChat
  actions: Array<StagedAction>
  onOpen: () => void
}) {
  const icon = isRegularChat(chat) ? (
    <MessageCircle className="size-4" />
  ) : chat.skillId ? (
    <ConversationSkillIcon skillId={chat.skillId} />
  ) : (
    <MessageSquareText className="size-4" />
  )

  return (
    <CellPreviewCard.Shell icon={icon} title={chat.title ?? 'Untitled chat'} onOpen={onOpen}>
      {chat.preview && (
        <div className="corner-smooth rounded-lg border p-3">
          <ActionContentPreview content={chat.preview} />
        </div>
      )}
      {actions.length > 0 && (
        <div className="divide-border corner-smooth flex flex-col divide-y rounded-lg border">
          {actions.map((action) => (
            <ConversationActionPreview key={action.id} action={action} />
          ))}
        </div>
      )}
    </CellPreviewCard.Shell>
  )
}

function ConversationActionPreview({ action }: { action: StagedAction }) {
  const answerable = isAnswerableStagedAction(action)
  // Both asks preview their choices; the resolver decides which one this is.
  const options = answerable ? answerableOptionGroups(action, { hideSingleQuestionPrompt: true }) : null

  return (
    <div className="flex items-start gap-2.5 p-3">
      {answerable ? (
        <AskUserBadge variant={askUserCardVariant(action)} />
      ) : (
        <ActionIconSlot view={action.view}>{getActionIcon(action)}</ActionIconSlot>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-foreground type-ui-sm truncate font-medium">{getActionTitle(action)}</p>
        {options ? (
          <div className="mt-2">
            <AskUserOptionsPreview {...options} />
          </div>
        ) : action.view?.content ? (
          <ActionContentPreview content={action.view.content} attachments={action.view.attachments} />
        ) : null}
      </div>
    </div>
  )
}
